
import React from "react";

import CameraHeightDisplay from "./CameraHeightDisplay";
import CoordinateDisplay from "./CoordinateDisplay";
import OrientationDisplay from "./OrientationDisplay";
import CameraSpeedDisplay from "./CameraSpeedDisplay";

import { Math as CesiumMath, Cartesian3, JulianDate } from "@cesium/engine";
import { useCesiumContext } from "../cesium/CesiumContext";

import { useOnCameraChanged, useOnCameraHeightChanged, useOnGamepadSelected, useOnNavigationSpeedFactorChange } from "../cesium/map";
import { formatUnitValue } from "../../lib/convert";
import { LabeledValue } from "../base/LabeledValue";
import { isNil } from "lodash-es";
import { useClockOnTick } from "../clock";
import { LengthUnits } from "convert-units";


const prevCameraPositionAndTime: {
    position?: Cartesian3,
    time?: JulianDate,
  } = {};
  function CameraPosition({showSpeed = true}) {
    const { map: viewer } = useCesiumContext();
  
    const [cameraPosition, setCameraPosition] = React.useState<
      [number, number, number] | undefined
    >(() => {
      if (!viewer || viewer.isDestroyed()) return;
      const { cartographicCamera } = viewer;
      return [
        CesiumMath.toDegrees(cartographicCamera.longitude),
        CesiumMath.toDegrees(cartographicCamera.latitude),
        cartographicCamera.height,
      ];
    });
    const [cameraHeightFromGround, setCameraHeightFromGround] = React.useState<
      number  | undefined
    >(() => {
      if (!viewer || viewer.isDestroyed() || isNil(viewer.terrainHeightAtPos))
        return;
      return viewer.cartographicCamera.height - viewer.terrainHeightAtPos;
    });
    const [cameraOrientation, setCameraOrientation] = React.useState<
      [number, number, number]
    >(() => {
      let regularTerrainCamera = viewer.adjustCameraOrientation(viewer.camera);
      return [
        CesiumMath.toDegrees(regularTerrainCamera.roll),
        CesiumMath.toDegrees(regularTerrainCamera.pitch),
        CesiumMath.toDegrees(regularTerrainCamera.heading),
      ];
    });
    const [cameraCartesian, setCameraCartesian] = React.useState<
      [number, number, number]
    >(() => {
      const cameraPos = viewer.adjustCartesianInv(viewer.camera.positionWC);
      return Cartesian3.pack(cameraPos, [], 0) as [number, number, number];
    });
    const [cameraSpeed, setCameraSpeed] = React.useState<
      number | undefined
    >(() => {
      if (!viewer || viewer.isDestroyed())
        return;
      return 0;
    });
    const [gamepadSelected, setGamepadSelected] = React.useState<number>( () => {
      if (!viewer || viewer.isDestroyed() || !viewer.navControls)
        return -1;
      return viewer.navControls.gamepadIndex;
    });
    const [gamePadSpeedSensitivity, setGamePadSpeedSensitivity] = React.useState<number>(() => {
      if (!viewer || viewer.isDestroyed() || !viewer.navControls)
        return -1;
      return viewer.navControls.speedChangeFactor - viewer.navControls.MIN_SPEED_FACTOR + 1;
    });
    const prevOrientation = React.useRef<any[]>([]);
    useClockOnTick(() => {
      if (!viewer || viewer.isDestroyed() || !viewer?.scene?.camera) return;
      if (!viewer.navControls?.isNavigating) {
        setCameraSpeed(undefined);
      } else {
        if (prevCameraPositionAndTime.position && prevCameraPositionAndTime.time) {
          const currTime = JulianDate.now();
          const elapsedTime = JulianDate.secondsDifference(currTime, prevCameraPositionAndTime.time); // sec
          // do not update too often
          if (elapsedTime >= 2.0) {
            const currCameraPos = viewer.adjustCartesianInv(viewer.camera.positionWC);
            setCameraSpeed(
              Cartesian3.distance(currCameraPos, prevCameraPositionAndTime.position) / elapsedTime); // m/s
            // store camera position
            prevCameraPositionAndTime.position = Cartesian3.clone(currCameraPos);
            prevCameraPositionAndTime.time = JulianDate.clone(currTime);
          }
        } else {
          // initialize camera position
          prevCameraPositionAndTime.position = Cartesian3.clone(viewer.adjustCartesianInv(viewer.camera.positionWC));
          prevCameraPositionAndTime.time = JulianDate.clone(JulianDate.now());
        }
      }
    }, 1000);
    useOnCameraChanged(() => {
      const { camera, cartographicCamera } = viewer;
      setCameraPosition([
        CesiumMath.toDegrees(cartographicCamera.longitude),
        CesiumMath.toDegrees(cartographicCamera.latitude),
        cartographicCamera.height,
      ]);
      // get camera elevation from ground
      const terrainCarto = viewer.scene.globe.ellipsoid.cartesianToCartographic(camera.positionWC);
      // Note: use carto coords in current reference system for retrieving terrain height
      let terrainHeightAtPos = viewer.scene.globe.getHeight(terrainCarto);
      if (isNil(terrainHeightAtPos)) {
        // try to use the viewer estimated terrain height
        // (this is more precise but can be out of sync)
        terrainHeightAtPos = viewer.terrainHeightAtPos;
      }
  
      const heightG = !isNil(terrainHeightAtPos)
        ? viewer.cartographicCamera.height - terrainHeightAtPos
        : undefined;
      setCameraHeightFromGround(heightG);
      
      const cameraPos = viewer.adjustCartesianInv(viewer.camera.positionWC);
      setCameraCartesian(
        Cartesian3.pack(cameraPos, [], 0) as [number, number, number]
      );
      const currentOrientation = [camera.roll, camera.pitch, camera.heading, viewer.isPolarCentric];
      if (currentOrientation.every((v, i) => CesiumMath.equalsEpsilon(+v, +prevOrientation.current[i], CesiumMath.EPSILON2))) return;
      const regularTerrainCamera = viewer.adjustCameraOrientation(viewer.camera);
      setCameraOrientation([
        CesiumMath.toDegrees(regularTerrainCamera.roll),
        CesiumMath.toDegrees(regularTerrainCamera.pitch),
        CesiumMath.toDegrees(regularTerrainCamera.heading),
      ]);
      prevOrientation.current = currentOrientation;
    });
    useOnCameraHeightChanged(() => {
      // get actual camera position
      // Note: viewer.cartographicCamera might be out of sync
      const cartoPos = viewer.getCameraCartoPos();
      const heightG = !isNil(viewer.terrainHeightAtPos)
        ? cartoPos.height - viewer.terrainHeightAtPos
        : undefined;
      setCameraHeightFromGround(heightG);
    });
    useOnGamepadSelected( (gamepadId) => {
      setGamepadSelected(gamepadId);
    })
    useOnNavigationSpeedFactorChange( (factor) => {
      if (!viewer || !viewer.navControls || viewer.isDestroyed()) return;
      const value = viewer.navControls.speedChangeFactor - viewer.navControls.MIN_SPEED_FACTOR + 1;
      setGamePadSpeedSensitivity(value);
    })
  
    return (
      <>
        <CoordinateDisplay value={cameraPosition} />
        <CameraHeightDisplay value={cameraHeightFromGround} />
        <CartesianDisplay value={cameraCartesian} />
        <OrientationDisplay value={cameraOrientation} />
        {showSpeed && 
            <CameraSpeedDisplay value={cameraSpeed} gamepadSelected={gamepadSelected} gamepadSpeedSensitivity={gamePadSpeedSensitivity}/>
        }
      </>
    );
  }
  
  export function CartesianDisplay({
    value,
    showLabels = true,
    units = "m",
    showUnits = false,
  }: {
    value: [number, number, number] | undefined;
    showLabels?: boolean;
    units?: LengthUnits;
    showUnits?: boolean;
  }) {
    return (
      <div className="flex items-center space-x-4">
        <LabeledValue
          minWidth="8em"
          label={showLabels && "X"}
          value={formatUnitValue(value?.[0], { from: "m", to: units, precision: 3, showUnits })}
        />
        <LabeledValue
          minWidth="8em"
          label={showLabels && "Y"}
          value={formatUnitValue(value?.[1], { from: "m", to: units, precision: 3, showUnits })}
        />
        <LabeledValue
          minWidth="8em"
          label={showLabels && "Z"}
          value={formatUnitValue(value?.[2], { from: "m", to: units, precision: 3, showUnits})}
        />
      </div>
    );
  }

  export default CameraPosition;